import React from 'react';
import { Container, Card, Badge } from '../design-system';
import { Layout, Smartphone, Plug, Palette, ClipboardList, UploadCloud } from 'lucide-react';
import { useI18n } from '../i18n/I18nProvider';

export function Services() {
  const { t } = useI18n();
  const services = [
    {
      icon: Layout,
      title: t('services.items.web.title'),
      description: t('services.items.web.desc'),
      technologies: ['React', 'Next.js', 'TypeScript'],
      image: '/images/sistema-web.png'
    },
    {
      icon: Smartphone,
      title: t('services.items.mobile.title'),
      description: t('services.items.mobile.desc'),
      technologies: ['React Native', 'Flutter', 'Swift'],
      image: '/images/mobile.png'
    },
    {
      icon: Plug,
      title: t('services.items.backend.title'),
      description: t('services.items.backend.desc'),
      technologies: ['Node.js', 'Python', 'PostgreSQL'],
      image: '/images/integracoes.png'
    },
    {
      icon: Palette,
      title: t('services.items.consulting.title'),
      description: t('services.items.consulting.desc'),
      technologies: ['Design Systems', 'Accessibility', 'Figma'],
      image: '/images/design.png'
    },
    {
      icon: ClipboardList,
      title: t('services.items.preproject.title'),
      description: t('services.items.preproject.desc'),
      technologies: ['Scope', 'Requirements', 'Roadmap'],
      image: '/images/pre-projeto.png'
    },
    {
      icon: UploadCloud,
      title: t('services.items.devops.title'),
      description: t('services.items.devops.desc'),
      technologies: ['CI/CD', 'App Stores', 'Cloud'],
      image: '/images/devops.png'
    }
  ];

  return (
    <section id="services" className="py-20 bg-slate-50">
      <Container maxWidth="xl">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-slate-900 mb-4">
            {t('services.heading')}
          </h2>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto">
            {t('services.description')}
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          {services.map((service, index) => {
            const Icon = service.icon;
            return (
              <Card key={index} padding="none" hover>
                <div className="grid md:grid-cols-2 gap-0">
                  <div className="p-8">
                    <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center mb-4">
                      <Icon className="w-6 h-6 text-indigo-600" />
                    </div>
                    <h3 className="text-2xl font-semibold text-slate-900 mb-3">
                      {service.title}
                    </h3>
                    <p className="text-slate-600 mb-4">
                      {service.description}
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {service.technologies.map((tech) => (
                        <Badge key={tech} variant="secondary" size="sm">
                          {tech}
                        </Badge>
                      ))}
                    </div>
                  </div>
                  <div className="h-56 md:h-64 lg:h-64">
                    {service.image.startsWith('http') ? (
                      <picture>
                        <source
                          type="image/avif"
                          srcSet={`${service.image}&fm=avif&q=70&w=480 480w, ${service.image}&fm=avif&q=70&w=777 777w, ${service.image}&fm=avif&q=70&w=960 960w`}
                          sizes="(min-width:1024px) 50vw, 100vw"
                        />
                        <source
                          type="image/webp"
                          srcSet={`${service.image}&fm=webp&q=70&w=480 480w, ${service.image}&fm=webp&q=70&w=777 777w, ${service.image}&fm=webp&q=70&w=960 960w`}
                          sizes="(min-width:1024px) 50vw, 100vw"
                        />
                        <img
                          src={`${service.image}&fm=jpg&q=80&w=777`}
                          alt={service.title}
                          className="w-full h-full object-cover rounded-r-xl"
                          loading="lazy"
                          decoding="async"
                          onError={(e) => {
                            e.currentTarget.src = 'https://images.unsplash.com/photo-1506765515384-028b60a970df?fm=jpg&q=80&w=777';
                          }}
                        />
                      </picture>
                    ) : (
                      <picture>
                        <source
                          type="image/webp"
                          srcSet={service.image.replace(/\.(png|jpg|jpeg)$/, '.webp')}
                        />
                        <img
                          src={service.image}
                          alt={service.title}
                          className="w-full h-full object-cover rounded-r-xl"
                          loading="lazy"
                          decoding="async"
                        />
                      </picture>
                    )}
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      </Container>
    </section>
  );
}
